<template>
  <div class="ai-assistant-settings">
    <div v-if="isLoading" class="text-center py-5">
      <b-spinner label="กำลังโหลด" />
    </div>
    <b-alert v-else-if="accessDenied" show variant="warning">
      คุณไม่มีสิทธิ์จัดการ AI Assistant ของคลินิกนี้ (ต้องเป็น C-Level หรือผู้จัดการทุกสาขาของคลินิก)
    </b-alert>
    <b-alert v-else-if="loadError" show variant="danger">
      {{ loadError }}
      <b-button size="sm" variant="outline-danger" class="ml-2" @click="load">ลองใหม่</b-button>
    </b-alert>
    <template v-else-if="setting">
      <b-alert show variant="info" class="mb-3">
        <i class="fas fa-user-shield mr-1"></i>
        ใช้ได้เฉพาะผู้จัดการสาขาและผู้บริหาร — AI อ่านข้อมูลได้อย่างเดียวตามสิทธิ์ของผู้ใช้แต่ละคน
      </b-alert>

      <b-card class="shadow-sm border-0 mb-3">
        <h5 class="mb-3"><i class="fas fa-toggle-on mr-2"></i>สถานะการใช้งาน</h5>
        <McpPackageStatus :clinic-url="clinicUrl" :setting="setting" :usage="usage" @changed="onSettingChanged" />
      </b-card>

      <b-card class="shadow-sm border-0 mb-3">
        <h5 class="mb-3"><i class="fas fa-link mr-2"></i>วิธีเชื่อมต่อ</h5>
        <McpConnectGuide :connector-url="setting.connectorUrl" :available="setting.active" />
      </b-card>

      <b-card class="shadow-sm border-0 mb-3">
        <h5 class="mb-3"><i class="fas fa-layer-group mr-2"></i>กลุ่มหัตถการ</h5>
        <McpProcedureGroups :clinic-url="clinicUrl" />
      </b-card>

      <b-card class="shadow-sm border-0 mb-3">
        <h5 class="mb-3"><i class="fas fa-plug mr-2"></i>ผู้ใช้ที่เชื่อมต่อ</h5>
        <McpConnections :clinic-url="clinicUrl" @loaded="onConnectionsLoaded" />
      </b-card>

      <b-card class="shadow-sm border-0 mb-3">
        <h5 class="mb-3"><i class="fas fa-history mr-2"></i>บันทึกการใช้งาน</h5>
        <McpAuditLog :clinic-url="clinicUrl" :users="connectionUsers" />
      </b-card>
    </template>
  </div>
</template>

<script>
import McpPackageStatus from "@/components/mcp/McpPackageStatus.vue";
import McpConnectGuide from "@/components/mcp/McpConnectGuide.vue";
import McpProcedureGroups from "@/components/mcp/McpProcedureGroups.vue";
import McpConnections from "@/components/mcp/McpConnections.vue";
import McpAuditLog from "@/components/mcp/McpAuditLog.vue";
import { mcpErrorMessage } from "@/components/mcp/mcpErrors";

export default {
  name: "AiAssistantSettings",
  components: { McpPackageStatus, McpConnectGuide, McpProcedureGroups, McpConnections, McpAuditLog },
  props: {
    clinicUrl: { type: String, required: true },
  },
  data() {
    return {
      isLoading: false,
      loadError: "",
      accessDenied: false,
      setting: null,
      usage: null,
      connectionUsers: [],
    };
  },
  // Note: no `watch: clinicUrl` here — the parent views key this component by
  // clinicUrl (see Setting.vue / ClinicAiAssistant.vue), so a clinic switch
  // fully remounts it instead of relying on a watcher to reload.
  created() {
    this.load();
  },
  methods: {
    async load() {
      this.isLoading = true;
      this.loadError = "";
      this.accessDenied = false;
      try {
        this.setting = await this.$store.dispatch("moduleMcpSetting/fetchSetting", { clinicUrl: this.clinicUrl });
      } catch (error) {
        if (error?.response?.status === 403) {
          this.accessDenied = true;
        } else {
          this.loadError = mcpErrorMessage(error, "โหลดการตั้งค่า AI Assistant ไม่สำเร็จ");
        }
        this.isLoading = false;
        return;
      }
      // Usage is best-effort: a failure here must not block the rest of the
      // page (setting/procedure groups/connections/audit log stay usable).
      try {
        this.usage = await this.$store.dispatch("moduleMcpSetting/fetchUsage", { clinicUrl: this.clinicUrl });
      } catch (error) {
        this.usage = null;
      }
      this.isLoading = false;
    },
    onSettingChanged(setting) {
      this.setting = setting;
    },
    onConnectionsLoaded(items) {
      // User filter options for the usage log: everyone listed in the connections table.
      const known = new Map(this.connectionUsers.map(user => [user.uid, user]));
      items.forEach(item => known.set(item.user.uid, item.user));
      this.connectionUsers = [...known.values()];
    },
  },
};
</script>
